import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import { Button, Select, TextInput } from 'lego-on-react';
import * as React from 'react';
import { KeyboardEvent } from 'react';

import { Bem } from '../../service/Bem/Bem';
import { Rate } from '../../service/Experiments/Experiments';
import { KeyCode } from '../../typings/common';

import './RateEditor.css';
import * as keyset from './RateEditor.i18n';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);

interface RateEditorProps {
    id: number;
    value: number | undefined;
    min?: number;
    max?: number;
    onSave: (data: Rate) => void;
    onCancel: () => void;
}

interface RateEditorState {
    value: number | undefined;
    change: 'up' | 'down';
}

export class RateEditor extends React.Component<RateEditorProps, RateEditorState> {
    public static defaultProps: Partial<RateEditorProps> = {
        min: -100,
        max: 1200
    };

    constructor(props: RateEditorProps) {
        super(props);

        this.state = {
            value: Math.abs(props.value || 0),
            change: !props.value || props.value >= 0 ? 'up' : 'down'
        };
    }

    render() {
        const { value, change } = this.state;
        const { min, max } = this.props;
        const isValid = this.isStateValid();

        return (
            <div className={Bem.cls('rate-editor')}>
                <div className={Bem.cls('rate-editor', 'form')}>
                    {i18nRaw('rate-string', {
                        change: (
                            <Select
                                cls={Bem.cls('rate-editor', 'menu')}
                                theme="normal"
                                size="s"
                                type="radio"
                                placeholder={i18n(change)}
                                onChange={this.onSelectChange}
                                val={change}
                                popup={{
                                    hiding: false
                                }}
                                items={[
                                    {
                                        text: i18n('up'),
                                        val: 'up'
                                    },
                                    {
                                        text: i18n('down'),
                                        val: 'down'
                                    }
                                ]}
                            />
                        ),
                        percent: (
                            <TextInput
                                theme="normal"
                                size="s"
                                view="classic"
                                placeholder={change === 'up' ? `max ${max}` : `min ${Math.abs(min!)}`}
                                cls={Bem.cls('rate-editor', 'input')}
                                text={!value ? '' : String(value)}
                                onChange={this.onInputChange}
                                onKeyDown={this.onInputKeydown}
                                hasClear={true}
                                autoFocus={true}
                            />
                        )
                    })}

                    <div className={Bem.cls('rate-editor', 'controls')}>
                        <Button
                            cls={Bem.cls('rate-editor', 'button')}
                            theme={'normal'}
                            size={'s'}
                            disabled={!isValid}
                            onClick={this.onSaveClick}
                        >
                            {i18n('save')}
                        </Button>
                        <Button
                            cls={Bem.cls('rate-editor', 'button')}
                            theme={'normal'}
                            size={'s'}
                            onClick={this.onCancelClick}
                        >
                            {i18n('cancel')}
                        </Button>
                    </div>
                </div>
                {!isValid && (
                    <div className={Bem.cls('rate-editor', 'error')}>
                        {i18n('error', {
                            change: i18n(change),
                            percent: change === 'up' ? max : Math.abs(min!)
                        })}
                    </div>
                )}
            </div>
        );
    }

    private isStateValid = () => {
        const { max, min } = this.props;
        const value = this.state.value || 0;
        let isValid = true;

        if (this.state.change === 'up') {
            isValid = value <= max!;
        } else {
            isValid = value <= Math.abs(min!);
        }

        return isValid;
    };

    private onInputChange = (value: string) => {
        const newValue = Number(value);

        if (value === '-') {
            this.setState({
                change: 'down'
            });
        }
        if (value === '+') {
            this.setState({
                change: 'up'
            });
        }
        if (!isNaN(newValue)) {
            this.setState({
                value: newValue
            });
        }
    };

    private onInputKeydown = (event: KeyboardEvent<HTMLElement>) => {
        if (event.keyCode === KeyCode.RETURN && this.isStateValid()) {
            this.onSaveClick();
        }
    };

    private onSelectChange = (values: Array<'up' | 'down'>) => {
        this.setState({
            change: values[0]
        });
    };

    private onSaveClick = () => {
        const { change, value } = this.state;
        const convertedValue = value && change === 'down' ? 0 - value : value;

        this.props.onSave({
            id: this.props.id,
            value: convertedValue
        });
    };

    private onCancelClick = () => {
        this.props.onCancel();
    };
}
